import { sumBy, keyBy, values, maxBy, compact } from 'lodash';

import {
	radarHighToAverageBoundariesRatio,
	radarAverageToLowBoundariesRatio,
	radarLikelyToUndefinedBoundariesRatio
} from './radarBoundaries';

import {
	getItemAlteredByScenario,
	isItemInactive,
	vulnerabilityIndicators,
	vulnerabilityStakes
} from './rules';

export const getScoreColor = (value: number) => {
	let color2 = [231, 5, 73];
	let color1 = [0, 176, 169];

	var w1 = value / 100;
	var w2 = 1 - w1;
	var rgb = [
		Math.round(color1[0] * w1 + color2[0] * w2),
		Math.round(color1[1] * w1 + color2[1] * w2),
		Math.round(color1[2] * w1 + color2[2] * w2)
	];
	return 'rgb(' + rgb.join(',') + ')';
};

export const getItemScenarioScore = (item: any, scenario: any) => {
	return vulnerability(
		[item].map((s: any) => getItemAlteredByScenario(s, scenario))
	);
};

export const getItemScore = (item: any, evaluations: any) => {
	let indexedEvaluations = keyBy(evaluations, 'itemId') || {};

	if (indexedEvaluations[item.id]) return indexedEvaluations[item.id].value;

	return 50;
};

const red = [231, 5, 73];
const green = [0, 176, 169];
const white = [255, 255, 255];

export function colorToPercent(val) {
	if (val == 'orange') return 100 / 3;

	if (val == 'red') return 0;

	if (val == 'green') return 100;
	if (val == 'blue') return (100 * 2) / 3;

	if (val === null || val === 'undefined') {
		return 50;
	}

	return parseInt(val);
}

export function percentToHue(val: any, start, end) {
	return [
		((100 - val) * start[0] + val * end[0]) / 100,
		((100 - val) * start[1] + val * end[1]) / 100,
		((100 - val) * start[2] + val * end[2]) / 100
	];
}

export function percentToSingleHue(val: any, isPositive) {
	if (isPositive) {
		return percentToHue(val, white, green);
	}

	return percentToHue(val, red, white);
}

export function hue(val: any) {
	var h = percentToHue(val, red, green);
	return 'rgb(' + h[0] + ', ' + h[1] + ', ' + h[2] + ')';
}

export function singleHue(val: any, isPositive) {
	var h = percentToSingleHue(val, isPositive);
	return 'rgb(' + h[0] + ', ' + h[1] + ', ' + h[2] + ')';
}

export const getFishBoneBranchScore = (branch: any) => {
	// Filter out disabled items
	const enabledItems = (branch.items || []).filter(
		(item: any) => item.enabled !== false
	);

	const pointsWeight = sumBy(enabledItems, (p) => p.weight || 1);
	let result =
		enabledItems.length > 0
			? sumBy(enabledItems, (p) => {
					const completion = parseInt(p.completion) || 0;
					const score =
						p.trend !== null && p.trend !== undefined
							? parseInt(p.trend)
							: 50;
					const remaining = 100 - completion;
					const scoreOfRemaining = score * (remaining / 100);
					const finalScore = completion + scoreOfRemaining;

					return finalScore * (p.weight || 1);
				}) / pointsWeight
			: null;
	return result;
};

export const getFishboneScore = (branchs: any) => {
	// Filter out disabled branches
	const enabledBranchs = (branchs || []).filter(
		(b: any) => b.enabled !== false
	);

	let weight = 0;
	let branchScores = enabledBranchs.map((b) => {
		const score =
			b.items && b.items.length > 0 ? getFishBoneBranchScore(b) : null;
		weight += score !== null ? b.weight || 1 : 0;
		return score !== null ? score * (b.weight || 1) : null;
	});

	branchScores = compact(branchScores);

	return branchScores.length > 0
		? sumBy(branchScores, (v) => v) / weight
		: null;
};

export const getIndividualRiskScore = (element) => {
	let likelihood = parseFloat(element.likelihood || element.o?.likelihood);
	let impact = parseFloat(element.impact || element.o?.impact);
	return likelihood * impact * impact;
};

export const getRiskScore = (
	elements: any[],
	maxLikelihood: number,
	maxImpact: number,
	riskCategories?: any[]
) => {
	if (!elements || elements.length === 0) return null;

	// Filter out disabled elements
	const enabledElements = elements.filter((el) => el.enabled !== false);

	if (enabledElements.length === 0) return null;

	const minLikelihood = 1;
	const minImpact = 1;
	const min = minLikelihood * minImpact;
	const max = maxLikelihood * maxImpact;

	let sumScores = 0;
	let totalWeight = 0;

	// Créer un index des catégories pour un accès rapide
	const indexedCategories = riskCategories
		? riskCategories.reduce((acc, category) => {
				acc[category.id] = category;
				return acc;
			}, {})
		: {};

	for (const el of enabledElements) {
		// Récupérer le poids de la catégorie du risque, défaut à 1 si pas de catégorie
		const categoryWeight =
			el.riskCategoryId && indexedCategories[el.riskCategoryId]
				? indexedCategories[el.riskCategoryId].weight || 1
				: 1;

		const likelihood = parseFloat(el.likelihood || el.o?.likelihood || 1);
		const impact = parseFloat(el.impact || el.o?.impact || 1);
		const expected = likelihood * impact;
		const score = 1 - (expected - min) / (max - min);

		// Pondérer le score par le poids de la catégorie
		sumScores += (100 - score * 100) * categoryWeight;
		totalWeight += categoryWeight;
	}

	if (totalWeight === 0) return null;

	return 100 - sumScores / totalWeight;
};

export const getOpportunityScore = (
	elements: any,
	maxLikelihood,
	maxImpact,
	riskCategories?: any[]
) => {
	if (elements.filter((r) => r.isOpportunity).length == 0) {
		return null;
	}

	let result =
		100 - getRiskScore(elements, maxLikelihood, maxImpact, riskCategories);
	return result;
};

export const getStakeOrConstraintScore = (elements: any) => {
	if (!elements || elements.length == 0) {
		return null;
	}

	// Filter out disabled elements
	const enabledElements = elements.filter((el: any) => el.enabled !== false);

	if (enabledElements.length == 0) {
		return null;
	}

	var total_weight = 0;
	var total = 0;

	for (var i = 0; i < enabledElements.length; i++) {
		total_weight += parseInt(enabledElements[i].weight) || 1;

		total +=
			colorToPercent(enabledElements[i].trend) *
			(enabledElements[i].weight || 1);
	}

	let currentPercentage = total_weight != 0 ? total / total_weight : 50;

	return currentPercentage;
};

export const getStakeholderScoreForScenarioDetailsColor = (element: any) => {
	let activeness = { undefined: 0, likely: 7, low: 18, medium: 33, high: 50 }[
		itemActiveness(element.x, element.y)
	];

	return element.y < 0 ? -activeness + 50 : activeness + 50;
};

export const getStakeholderScoreForScenarioDetailsRanking = (element: any) => {
	let activeness = {
		undefined: 0,
		likely: 70,
		low: 180,
		medium: 330,
		high: 500
	}[itemActiveness(element.x, element.y)];

	let weight = { low: 0, medium: 5, high: 10 }[
		element.impact ? element.impact : 'low'
	];
	let trend = { green: 3, red: 0, orange: 2, neutral: 1 }[
		element.trend ? element.trend : 'neutral'
	];

	let score = activeness + weight + trend;
	if (element.links && element.links.length > 0) {
		let linksStrength = element.links
			? sumBy(element.links, (l) => {
					return l.strength * 0.1;
				})
			: 0;
		score += Math.min(linksStrength, 10) / 100;
	}

	return element.y < 0 ? -score + 500 : score + 500;
};

export const itemActiveness = function (x: Number, y: Number) {
	var d = Math.sqrt(x * x + y * y);

	if (isItemInactive({ x, y })) return 'undefined';

	if (d < radarHighToAverageBoundariesRatio * 100) return 'high';
	if (d < radarAverageToLowBoundariesRatio * 100) return 'medium';
	if (d <= 100) return 'low';
	if (d < radarLikelyToUndefinedBoundariesRatio * 100) return 'likely';
	return 'undefined';
};

const itemWeight = function (item) {
	var weight = 1,
		activeness = 0;

	weight = { low: 0.6, medium: 2, high: 4 }[item.impact || 'low'];

	activeness = {
		undefined: 0,
		likely: 0.45,
		low: 0.6,
		medium: 1.6,
		high: 3.5
	}[itemActiveness(item.x, item.y)];

	if (item.y > 0)
		weight *= { green: 1.2, red: 0.8, orange: 0.9, neutral: 1 }[
			item.trend ? item.trend : 'neutral'
		];
	else
		weight *= { green: 0.8, red: 1.2, orange: 1.1, neutral: 1 }[
			item.trend ? item.trend : 'neutral'
		];

	// Alters weight based on links
	if (item.links && item.links.length > 0) {
		let linksStrength = item.links
			? sumBy(item.links, (l) => {
					return l.strength * 0.4;
				})
			: 0;
		weight += (Math.min(linksStrength, 4) / 16) * weight;
	}

	return item.y > 0 ? weight * activeness : -weight * activeness;
};

const normalizeTotalWeight = function (total, sum_positive, sum_negative) {
	var max = Math.max(sum_positive, -sum_negative);
	var v = total;

	if (max != 0) v = v / max;
	v = -v * 80 + 50;
	v = Math.min(100, v);
	v = Math.max(0, v);

	return v;
};

export const vulnerability = function (items) {
	if (!items || items.length == 0) {
		return null;
	}

	// Filter out disabled items
	const enabledItems = items.filter((item) => item.enabled !== false);

	if (enabledItems.length == 0) {
		return null;
	}

	var total_weight = 0;
	var sum_negative = 0;
	var sum_positive = 0;

	for (var i = 0; i < enabledItems.length; i++) {
		var weight = itemWeight(enabledItems[i]);
		total_weight += weight;

		if (weight >= 0) {
			sum_positive += weight;
		} else {
			sum_negative += weight;
		}
	}

	return normalizeTotalWeight(total_weight, sum_positive, sum_negative);
};

export const scenarioScore = (project: any, scenario: any) => {
	let divisor = 0;
	let stakesScore = 0;
	let constraintsScore = 0;
	let stakeholdersScore = 0;
	let risksScore = 0;
	let opportunitiesScore = 0;
	let fishboneScore = 0;

	if ((project?.stakes || []).length > 0) {
		stakesScore = vulnerabilityStakes(
			(project?.stakes || []).map((i: any) =>
				getItemAlteredByScenario(i, scenario)
			)
		);

		divisor++;
	}

	if ((project?.indicators || []).length > 0) {
		constraintsScore = vulnerabilityIndicators(
			(project?.indicators || []).map((c: any) =>
				getItemAlteredByScenario(c, scenario)
			)
		);

		divisor++;
	}

	if ((project?.stakeholders || []).length > 0) {
		stakeholdersScore = vulnerability(
			(project?.stakeholders || []).map((s: any) =>
				getItemAlteredByScenario(s, scenario)
			)
		);

		divisor++;
	}

	if ((project.fishboneBranchs || []).length > 0) {
		let preparedData = project.fishboneBranchs.map((b: any) => {
			return {
				...b,
				items: b.items.map((i: any) =>
					getItemAlteredByScenario(i, scenario)
				)
			};
		});

		fishboneScore = getFishboneScore(preparedData);
		divisor++;
	}

	const maxLikelihood =
		(project?.likelihoods &&
			maxBy(values(project?.likelihoods), 'id')?.id) ||
		3;
	const maxImpact =
		(project?.impacts && maxBy(values(project?.impacts), 'id')?.id) || 3;

	const risks = (project?.risks || []).filter((r) => !r.isOpportunity);
	if (risks.length > 0) {
		risksScore = getRiskScore(
			risks.map((s: any) => getItemAlteredByScenario(s, scenario)),
			maxLikelihood,
			maxImpact,
			project?.riskCategories
		);

		divisor++;
	}

	const opportunities = (project?.risks || []).filter(
		(r) => !!r.isOpportunity
	);
	if (opportunities.length > 0) {
		opportunitiesScore = getOpportunityScore(
			opportunities.map((s: any) =>
				getItemAlteredByScenario(s, scenario)
			),
			maxLikelihood,
			maxImpact,
			project?.riskCategories
		);

		divisor++;
	}

	if (stakeholdersScore !== null && (project?.stakeholders || []).length > 0)
		stakeholdersScore = 100 - stakeholdersScore;

	let score =
		divisor > 0
			? (stakeholdersScore +
					stakesScore +
					constraintsScore +
					risksScore +
					opportunitiesScore +
					fishboneScore) /
				divisor
			: null;

	return score;
};
